// Pure helpers that flatten workflows.json into table rows.
// Read-only: never mutates inputs.

import {
  extractResources,
  matchRoute,
  normalizeProviderName,
  resolveMatchResources,
  resolveProviderName,
  routeProvidersMatch,
} from "./buildGraph";

// ---------------------------------------------------------------------------
// Provider index (id-keyed — names are not unique across the config)
// ---------------------------------------------------------------------------

/**
 * Every provider named by any route, keyed by id. Specialties are unioned
 * across ids sharing a display name, since production routes by name.
 * @returns {{ id: string, name: string, specialties: string[] }[]}
 */
export function collectAllProviders(workflowsList) {
  const byId = new Map();
  const specialtiesByName = new Map();

  for (const workflow of workflowsList || []) {
    for (const route of workflow?.routes || []) {
      for (const provider of route?.conditions?.providers || []) {
        if (!provider?.id) continue;
        let entry = byId.get(provider.id);
        if (!entry) {
          entry = { id: provider.id, name: provider.name, specialties: new Set() };
          byId.set(provider.id, entry);
        }
        // Prefer the longest spelling when an id's name varies by specialty.
        if (provider.name && provider.name.length >= (entry.name || "").length) {
          entry.name = provider.name;
        }
        entry.specialties.add(workflow.specialty);

        const nameKey = normalizeProviderName(provider.name);
        if (nameKey) {
          if (!specialtiesByName.has(nameKey)) specialtiesByName.set(nameKey, new Set());
          specialtiesByName.get(nameKey).add(workflow.specialty);
        }
      }
    }
  }

  for (const entry of byId.values()) {
    const extras = specialtiesByName.get(normalizeProviderName(entry.name));
    if (extras) {
      for (const specialty of extras) entry.specialties.add(specialty);
    }
  }

  return [...byId.values()]
    .map((entry) => ({
      id: entry.id,
      name: entry.name,
      specialties: [...entry.specialties].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** All distinct visit types across every specialty. */
export function collectAllVisitTypes(workflowsList) {
  const set = new Set();
  for (const workflow of workflowsList || []) {
    for (const route of workflow?.routes || []) {
      for (const type of route?.conditions?.patient_types || []) set.add(type);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

// ---------------------------------------------------------------------------
// Reachability: which routes can ever win for a given doctor
// ---------------------------------------------------------------------------

function routeAppliesToProvider(route, providerName) {
  return routeProvidersMatch(route?.conditions?.providers || [], providerName);
}

// A specialty is relevant if it names this doctor by id or normalized name;
// production matches on name, and id covers same-uuid spelling variants.
function workflowMentionsProvider(workflow, { providerId = null, providerName = null } = {}) {
  const cleaned = normalizeProviderName(providerName);
  return (workflow?.routes || []).some((r) =>
    (r?.conditions?.providers || []).some((p) => {
      if (providerId && p?.id === providerId) return true;
      return cleaned && normalizeProviderName(p?.name) === cleaned;
    })
  );
}

function powerSet(items) {
  // Flags max out around 6 per specialty, so 64 subsets at worst.
  const out = [[]];
  for (const item of items) {
    const n = out.length;
    for (let i = 0; i < n; i++) out.push([...out[i], item]);
  }
  return out;
}

/**
 * Which route indexes (and the default) can win for this doctor under some
 * visit-type × flag-subset input. The table hides the rest rather than showing
 * a route the doctor can never get.
 *
 * @returns {{ reachable: Set<number|"default"> }}
 */
export function computeReachability(workflow, { providerId = null, providerName = null } = {}) {
  const resolvedName = resolveProviderName(workflow, providerId, providerName);
  const routes = workflow?.routes || [];
  const visitTypes = new Set();
  const flags = new Set();
  for (const route of routes) {
    for (const t of route?.conditions?.patient_types || []) visitTypes.add(t);
    for (const f of route?.conditions?.flags || []) flags.add(f);
  }
  const typeOptions = [null, ...visitTypes];
  const flagSubsets = powerSet([...flags]);

  const reachable = new Set();

  for (const patientType of typeOptions) {
    for (const flagSubset of flagSubsets) {
      const matched = matchRoute(workflow, {
        providerName: resolvedName,
        patientType,
        flags: flagSubset,
      });
      if (matched.isDefault) {
        reachable.add("default");
      } else {
        reachable.add(matched.index);
      }
    }
  }

  return { reachable };
}

// ---------------------------------------------------------------------------
// Row building
// ---------------------------------------------------------------------------

function makeRouteRow(workflow, route, index) {
  const conditions = route?.conditions || {};
  return {
    key: `${workflow.specialty}:R${index}`,
    specialty: workflow.specialty,
    order: index,
    isDefault: false,
    providers: (conditions.providers || []).map((p) => ({
      id: p.id,
      name: p.name,
    })),
    types: conditions.patient_types || [],
    flags: conditions.flags || [],
    // Sparse routes inherit the full default_route set, as in production.
    resources: resolveMatchResources(
      route?.resources,
      workflow?.default_route?.resources
    ),
    isWildcard: (conditions.providers || []).length === 0,
  };
}

function makeDefaultRow(workflow) {
  return {
    key: `${workflow.specialty}:DEF`,
    specialty: workflow.specialty,
    order: null,
    isDefault: true,
    providers: [],
    types: [],
    flags: [],
    resources: extractResources(workflow?.default_route?.resources),
    isWildcard: false,
  };
}

/**
 * Flatten workflows into table rows.
 *
 * With a provider set, only their specialties are included and any route that
 * can never win for them is hidden, the default row included. Without one,
 * `patientType` / `flags` filter rows to those compatible with the pickers.
 *
 * @returns {{
 *   rows: object[],
 *   sections: { specialty: string, hiddenCount: number }[],
 *   hiddenTotal: number,
 * }}
 */
export function buildRouteRows(
  workflowsList,
  {
    specialty = null,
    providerId = null,
    providerName = null,
    patientType = null,
    flags = [],
  } = {}
) {
  const flagsSet = new Set(flags);
  const hasConditionFilter = Boolean(patientType) || flagsSet.size > 0;
  const hasProvider = Boolean(providerId || providerName);
  const rows = [];
  const sections = [];
  let hiddenTotal = 0;

  for (const workflow of workflowsList || []) {
    if (specialty && workflow.specialty !== specialty) continue;

    const routes = workflow?.routes || [];
    let hiddenCount = 0;

    if (hasProvider) {
      if (!workflowMentionsProvider(workflow, { providerId, providerName })) {
        continue;
      }

      const resolvedName = resolveProviderName(workflow, providerId, providerName);
      const { reachable } = computeReachability(workflow, {
        providerId,
        providerName,
      });

      for (let i = 0; i < routes.length; i++) {
        const route = routes[i];
        if (!routeAppliesToProvider(route, resolvedName)) continue;
        if (!reachable.has(i)) {
          hiddenCount += 1;
          continue;
        }
        rows.push(makeRouteRow(workflow, route, i));
      }

      if (workflow.default_route && reachable.has("default")) {
        rows.push(makeDefaultRow(workflow));
      } else if (workflow.default_route) {
        hiddenCount += 1;
      }
    } else {
      for (let i = 0; i < routes.length; i++) {
        const route = routes[i];
        const conditions = route?.conditions || {};
        const routeTypes = conditions.patient_types || [];
        const routeFlags = conditions.flags || [];

        if (hasConditionFilter) {
          const typeOk =
            !patientType ||
            routeTypes.length === 0 ||
            routeTypes.includes(patientType);
          const flagsOk =
            flagsSet.size === 0 ||
            routeFlags.length === 0 ||
            routeFlags.every((f) => flagsSet.has(f));
          if (!typeOk || !flagsOk) continue;
        }

        rows.push(makeRouteRow(workflow, route, i));
      }
      if (workflow.default_route) {
        if (!hasConditionFilter) rows.push(makeDefaultRow(workflow));
      }
    }

    const sectionRows = rows.filter((r) => r.specialty === workflow.specialty);
    if (sectionRows.length > 0 || (hasProvider && hiddenCount > 0)) {
      // Never emit a section with no visible rows, even if we hid some.
      if (sectionRows.length > 0) {
        sections.push({ specialty: workflow.specialty, hiddenCount });
        hiddenTotal += hiddenCount;
      }
    }
  }

  return { rows, sections, hiddenTotal };
}

// A null patient type means either "no visit described yet" (match nothing) or
// "a visit no route types" (match untyped routes); `anyPatientType` picks the
// latter.
function hasSomethingToMatch({ patientType, flags, anyPatientType }) {
  if (anyPatientType) return true;
  return patientType != null || (flags && flags.length > 0);
}

/**
 * The table-row identity to highlight. Null when no doctor is selected or no
 * visit has been described, since there is nothing to match against.
 *
 * @returns {{ specialty: string, order: number|null, isDefault: boolean }|null}
 */
export function findWinnerOrder(
  workflowsList,
  {
    specialty = null,
    providerId = null,
    providerName = null,
    patientType = null,
    flags = [],
    anyPatientType = false,
  } = {}
) {
  const match = findWorkflowMatch(workflowsList, {
    specialty,
    providerId,
    providerName,
    patientType,
    flags,
    anyPatientType,
  });
  if (!match) return null;
  return {
    specialty: match.specialty,
    order: match.isDefault ? null : match.index,
    isDefault: match.isDefault,
  };
}

/**
 * Resolve the production-effective workflow route and resources for one
 * doctor and visit. This is the detailed form of findWinnerOrder, shared by
 * pages that need the configured template rather than only a row highlight.
 *
 * When no specialty is supplied, workflow order decides which specialty wins,
 * matching the visualizer's existing behavior.
 */
export function findWorkflowMatch(
  workflowsList,
  {
    specialty = null,
    providerId = null,
    providerName = null,
    patientType = null,
    flags = [],
    anyPatientType = false,
    allowUnlistedProvider = false,
  } = {}
) {
  if (!providerId && !providerName) return null;
  if (!hasSomethingToMatch({ patientType, flags, anyPatientType })) return null;

  const targets = (workflowsList || []).filter((w) => {
    if (specialty && w.specialty !== specialty) return false;
    // A doctor can be moved into a specialty for a standalone demo before
    // they appear in that specialty's provider-specific routes. In that case,
    // production still evaluates wildcard routes and then the default route.
    if (allowUnlistedProvider && specialty) return true;
    return workflowMentionsProvider(w, { providerId, providerName });
  });

  if (targets.length === 0) return null;
  const workflow = targets[0];
  const matched = matchRoute(workflow, {
    providerName: resolveProviderName(workflow, providerId, providerName),
    patientType,
    flags,
  });
  return {
    specialty: workflow.specialty,
    ...matched,
  };
}

/**
 * Per-specialty winners, so each specialty section can highlight its own
 * matched row for a doctor who works in more than one.
 *
 * @returns {Map<string, { order: number|null, isDefault: boolean }>}
 */
export function findWinnersBySpecialty(
  workflowsList,
  {
    providerId = null,
    providerName = null,
    patientType = null,
    flags = [],
    anyPatientType = false,
  } = {}
) {
  const winners = new Map();
  if (!providerId && !providerName) return winners;
  if (!hasSomethingToMatch({ patientType, flags, anyPatientType })) {
    return winners;
  }

  for (const workflow of workflowsList || []) {
    if (!workflowMentionsProvider(workflow, { providerId, providerName })) {
      continue;
    }
    const matched = matchRoute(workflow, {
      providerName: resolveProviderName(workflow, providerId, providerName),
      patientType,
      flags,
    });
    winners.set(workflow.specialty, {
      order: matched.isDefault ? null : matched.index,
      isDefault: matched.isDefault,
    });
  }
  return winners;
}
